/**
 * ConnectProviderModal — intent-aware provider catalog modal.
 *
 * Replaces the bottom-of-page "Connect a utility provider" grid that used
 * to live under MethodStep. Triggered by the Entry-step toolbar's Connect
 * button. Reused unchanged for By Spend (accounting software) and By
 * Travel (travel platforms) once those flows activate — the section prop
 * carries everything the modal needs to render.
 *
 * Behaviour: each tile click fires `fireProviderRequestAccessToast` (no
 * navigation). The modal stays open so users can request access to
 * multiple providers in one sitting.
 */

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  PROVIDER_REGION_ORDER,
  REGION_LABELS,
  type IntentProviderSection,
  type ProviderEntry,
  type ProviderRegion,
} from '../requestAccessConfig';
import { ProviderTile } from '../ProviderTile';

export interface ConnectProviderModalProps {
  isOpen: boolean;
  /** What to render — title, hint, providers, region grouping, etc. */
  section: IntentProviderSection;
  onClose: () => void;
}

export const ConnectProviderModal = ({ isOpen, section, onClose }: ConnectProviderModalProps) => {
  const providersByRegion = new Map<ProviderRegion, ProviderEntry[]>();
  if (section.groupByRegion) {
    for (const provider of section.providers) {
      const bucket = providersByRegion.get(provider.region) ?? [];
      bucket.push(provider);
      providersByRegion.set(provider.region, bucket);
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='lg' scrollable>
      <ModalHeader toggle={onClose}>{section.title}</ModalHeader>
      <ModalBody>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>{section.hint}</p>

        {section.groupByRegion ? (
          <div className='tw:flex tw:flex-col tw:gap-5'>
            {PROVIDER_REGION_ORDER.filter((r) => providersByRegion.has(r)).map((region) => {
              const providers = providersByRegion.get(region)!;
              return (
                <div key={region}>
                  <p className='tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:mb-2'>
                    {REGION_LABELS[region]}
                  </p>
                  <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-2.5'>
                    {providers.map((provider) => (
                      <ProviderTile key={provider.id} provider={provider} method={section.catalogMethod} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-2.5'>
            {section.providers.map((provider) => (
              <ProviderTile key={provider.id} provider={provider} method={section.catalogMethod} />
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
